import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CategoryToken } from '@sleed/bizcommerce-api';
import { categoryAttributesMapApi } from '~/lib/api/client';

export const categoryAttributesMapKeys = {
	all: ['category-attributes-map'] as const,
	categories: () => [...categoryAttributesMapKeys.all, 'categories'] as const,
	attributes: () => [...categoryAttributesMapKeys.all, 'attributes'] as const,
	assigned: (cid: number) => [...categoryAttributesMapKeys.all, 'assigned', cid] as const
};

/** A mappable attribute (flattened from the API's option-group envelope). */
export interface MappableAttribute {
	id: number;
	name: string;
}

/** Category tree (flat list with `pid` parent pointers) — mirrors the legacy `/categoryattributesmap/getcategories`. */
export function useMapCategories() {
	return useQuery<CategoryToken[]>({
		queryKey: categoryAttributesMapKeys.categories(),
		queryFn: async () => (await categoryAttributesMapApi.getCategories()).data.items ?? []
	});
}

/** Every assignable attribute, flattened from the option-group envelope (the API returns one "[Empty]" group). */
export function useMappableAttributes() {
	return useQuery<MappableAttribute[]>({
		queryKey: categoryAttributesMapKeys.attributes(),
		queryFn: async () => {
			const groups = (await categoryAttributesMapApi.getAttributes()).data.items ?? [];
			return groups
				.flatMap(g => g.options ?? [])
				.map(o => ({ id: Number(o.value), name: o.label ?? '' }))
				.filter(a => Number.isFinite(a.id));
		}
	});
}

/** Attribute ids currently assigned to a category (flattened from the grouped envelope). */
export function useAssignedAttributes(categoryId: number | undefined) {
	return useQuery<number[]>({
		queryKey: categoryAttributesMapKeys.assigned(categoryId ?? 0),
		queryFn: async () => {
			const groups = (await categoryAttributesMapApi.getAttributesAssigned(categoryId)).data.items ?? [];
			return groups
				.flatMap(g => g.options ?? [])
				.map(o => Number(o.value))
				.filter(Number.isFinite);
		},
		enabled: Boolean(categoryId)
	});
}

/**
 * Persist a category's full attribute set. Mirrors the legacy save flow: the server's
 * CategoryAttributesMapController.Save replaces the category's mappings with the supplied set
 * (RemoveRange + Add), so we send the complete `attributeIds`. An empty set routes to
 * `deleteByCategory` (the legacy behaviour when the last attribute is removed).
 */
export function useSaveCategoryAttributes() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ categoryId, attributeIds }: { categoryId: number; attributeIds: number[] }) => {
			if (attributeIds.length === 0) {
				await categoryAttributesMapApi.deleteByCategory(categoryId);
			} else {
				await categoryAttributesMapApi.save({ items: attributeIds.map(aid => ({ cid: categoryId, aid })) });
			}
		},
		onSuccess: (_data, { categoryId }) => qc.invalidateQueries({ queryKey: categoryAttributesMapKeys.assigned(categoryId) })
	});
}
